import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export const DC = "/admin/data-center";
export const errMsg = (e, fb = "Something went wrong") => e?.response?.data?.error || e?.response?.data?.detail || fb;

export const STATUS_STYLE = {
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  incomplete: "bg-sky-50 text-sky-700 border-sky-200",
  duplicate: "bg-violet-50 text-violet-700 border-violet-200",
  review: "bg-orange-50 text-orange-700 border-orange-200",
  invalid: "bg-rose-50 text-rose-700 border-rose-200",
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-indigo-50 text-indigo-700 border-indigo-200",
  merge: "bg-indigo-50 text-indigo-700 border-indigo-200",
  skip: "bg-slate-100 text-slate-600 border-slate-200",
  created: "bg-emerald-50 text-emerald-700 border-emerald-200",
  updated: "bg-indigo-50 text-indigo-700 border-indigo-200",
  merged: "bg-indigo-50 text-indigo-700 border-indigo-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
};
const ACTION_LABEL = { create: "Will create", update: "Will update", merge: "Will merge", skip: "Will skip", review: "Needs review" };

export const Pill = ({ s, tid, children }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap ${STATUS_STYLE[s] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={tid}>
    {children || ACTION_LABEL[s] || String(s || "").replace(/_/g, " ")}
  </span>
);

export const Card = ({ children, className = "", tid }) => <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`} data-testid={tid}>{children}</div>;

export const Btn = ({ children, onClick, kind = "primary", disabled, busy, tid, className = "", type = "button" }) => {
  const k = { primary: "bg-[#165a71] text-white hover:bg-[#11485a]", ghost: "border border-slate-200 bg-white text-[#071b22] hover:border-[#165a71]/40", danger: "bg-rose-600 text-white hover:bg-rose-700", accent: "bg-[#d97757] text-white hover:bg-[#c4664a]" }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled || busy} className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${k} ${className}`} data-testid={tid}>
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
    </button>
  );
};

export const Sel = ({ value, onChange, options, tid, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#165a71] ${className}`} data-testid={tid}>
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);

export const Inp = (p) => <input {...p} className={`rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71] ${p.className || ""}`} />;

export const Head = ({ title, sub, children }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22]">{title}</h1>
      {sub && <p className="text-sm text-[#252A34]/65 mt-1 max-w-3xl">{sub}</p>}
    </div>
    <div className="flex gap-2 flex-wrap">{children}</div>
  </div>
);

export const Bar = ({ pct, label }) => (
  <div>
    <div className="flex justify-between text-xs text-[#252A34]/70 mb-1"><span>{label}</span><span>{pct ?? 0}%</span></div>
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#165a71] transition-[width] duration-500" style={{ width: `${pct || 0}%` }} /></div>
  </div>
);

export const Empty = ({ children, tid }) => <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-[#252A34]/60" data-testid={tid}>{children}</div>;

export async function download(method, url, body, fallbackName) {
  const r = method === "get" ? await api.get(url, { responseType: "blob" }) : await api.post(url, body, { responseType: "blob" });
  const name = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || fallbackName;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
  return Number(r.headers["x-row-count"] || 0);
}

export function usePoll(fn, active, ms = 1500) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [active, ms]);
}

export function useMeta(url = `${DC}/meta`) {
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get(url).then((r) => setMeta(r.data)).catch(() => setMeta({ error: true })); }, [url]);
  return meta;
}

export const ENTITY_OPTS = [["providers", "Providers"], ["services", "Services"], ["deals", "Deals"]];
export const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
